import { NavArrowDown } from 'iconoir-react'
import classNames from 'classnames'
import { Info } from 'app/components/Info'
import type { FunnelSection } from './FunnelChart'

// Bar colours deepen down the funnel, mirroring the SVG funnel's blue ramp. Opacity steps on
// one shade rather than a 300->800 shade ramp: shades 700-950 lighten and 50-300 darken in
// dark mode (see dark-hues.css), which would reverse the deepening order.
const BAR_COLORS = [`bg-blue-600/35`, `bg-blue-600/55`, `bg-blue-600/75`, `bg-blue-600`]

function stepPercent(prev?: number, curr?: number): number {
  if (!prev || curr == null || prev <= 0 || curr < 0) return 0
  return Math.round((curr / prev) * 100)
}

/**
 * Mobile-native funnel: the same stages the SVG FunnelChart draws, but stacked
 * vertically. Each stage is a tappable row with a proportional bar, its value
 * (or step conversion % in `percentageView`), and a trend badge; a small "↓ %"
 * connector between rows shows stage-to-stage conversion. Takes the identical
 * `sections` the SVG funnel uses, so it's a drop-in swap below md.
 */
export function FunnelList({
  sections,
  percentageView = false,
  isLoading = false,
}: {
  sections: FunnelSection[]
  percentageView?: boolean
  isLoading?: boolean
}) {
  const firstValue = sections[0]?.value ?? 0

  return (
    <div className="flex flex-col">
      {sections.map((section, i) => {
        const value = section.value ?? 0
        const barPct = firstValue > 0 ? Math.max((value / firstValue) * 100, 2) : 0
        const step = i === 0 ? 100 : stepPercent(sections[i - 1]?.value, value)
        const primary = percentageView ? (i === 0 ? `100%` : `${step}%`) : value.toLocaleString(`en-US`)
        return (
          <div key={section.label}>
            {i > 0 && (
              <div className="flex items-center gap-1 py-1 pl-2 text-body-xs font-medium text-minimal">
                <NavArrowDown className="h-3.5 w-3.5" strokeWidth={2} />
                {step}%
              </div>
            )}
            <button
              type="button"
              onClick={section.onDetailClick}
              className="group flex w-full flex-col gap-2 rounded-lg px-2 py-3 text-left transition-colors hover:bg-surface-subtle"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-body-sm font-medium text-default">
                    {percentageView ? section.altLabel : section.label}
                  </span>
                  {section.infoTooltip && (
                    <Info title={section.infoTooltip.title} message={section.infoTooltip.message} />
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-heading-sm tabular-nums text-strong">{primary}</span>
                </div>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-muted">
                <div
                  className={classNames(`h-full rounded-full transition-all`, BAR_COLORS[i % BAR_COLORS.length])}
                  style={{ width: `${barPct}%` }}
                />
              </div>
              {!percentageView && section.subLabel && (
                <span className="text-body-xs text-subtle">{section.subLabel}</span>
              )}
            </button>
          </div>
        )
      })}
    </div>
  )
}
